import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import MarketingTags from '@/components/MarketingTags';
import { createPageUrl } from '@/utils';
import { User } from '@/api/entities';
import { FlightBooking } from '@/api/entities';
import { AbandonedTrip } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Plus, MapPin, Calendar, Sparkles, Plane, Search as SearchIcon, Users, DollarSign, Clock, Calendar as CalendarIcon, Check, CheckCircle, Award, PlaneTakeoff, ArrowLeft, ArrowRight, Armchair, Wine, Gift, Zap, RotateCcw, XCircle, RefreshCw, CreditCard } from 'lucide-react';
import FlightOfferCard from '../components/search/FlightOfferCard';
import { buildFlightSearchParams, prefetchFlightSearch } from '@/lib/searchPrefetch';
import { initialSearchTravelers } from '@/lib/usualTravelers';
import { useSearchPickers } from '@/hooks/useSearchPickers';
import { checkBuildWhileSearching, openSearchResults } from '@/lib/searchNavigation';
import { writePassengerParams } from '@/utils/passengerTypes';
import { nextSearchDateRange } from '@/utils/searchDateRange';
import { normalizeCabinClass } from '@/utils/cabinClass';
import DestinationModal from '../components/destinations/DestinationModal';
import AirportInput from '../components/search/AirportInput';
import PassengerSelector from '../components/search/PassengerSelector';
import CabinClassSelector from '../components/search/CabinClassSelector';
import PaymentMethodSelector from '../components/search/PaymentMethodSelector';
import { getCityName } from '../components/airportUtils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarWithPrices as CalendarComponent, CalendarRangeStyles } from '@/components/ui/CalendarWithPrices';
import CalendarDock from '@/components/ui/CalendarDock';

/** First selectable departure day (22 days out) — calendars open here instead of a fully-disabled current month. */
const getMinBookableDate = () => new Date(new Date().setDate(new Date().getDate() + 22));

import { useFlightCalendarPrices } from '@/hooks/useFlightCalendarPrices';
import { format, formatDistanceToNow } from 'date-fns';
import { UserPreferences } from '@/api/entities';
import { useMilesBalanceRealtime } from '@/hooks/useMilesBalanceRealtime';
import { useUserStatus } from '@/hooks/useUserStatus';
import { useReserveEligibility } from '@/hooks/useReserveEligibility';
import StatusProgressCard from '@/components/status/StatusProgressCard';
import SectionBand from '@/components/ui/SectionBand';
import { TIER_THEME, getTier } from '@/constants/tiers';
import { generateSearchActivity } from '@/data/searchActivityData';
import { isNativeApp } from '@/native/bootstrap';
import BeautyOfFreedomVideo from '@/components/marketing/BeautyOfFreedomVideo';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import TripNowCard from '@/components/flight/TripNowCard';
import { SHOW_REFUNDABLE_PERKS_CARDS } from '@/constants/promoCards';
import MyVipBadge from '@/components/vip/MyVipBadge';

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [recentTrips, setRecentTrips] = useState([]);
  const [upcomingFlights, setUpcomingFlights] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [showDestinationModal, setShowDestinationModal] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  // Quick search widget state
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [date, setDate] = useState({ from: undefined, to: undefined });
  // Opens at the party this customer usually searches for (usualTravelers.js).
  const [passengers, setPassengers] = useState(initialSearchTravelers);
  const [cabinClass, setCabinClass] = useState('economy');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { isTravelersOpen, onTravelersOpenChange, earlySearchDelay } = useSearchPickers({ isCalendarOpen });
  const [isMobile, setIsMobile] = useState(false);
  const [numberOfMonths, setNumberOfMonths] = useState(2);
  const [tripMode, setTripMode] = useState('flight');
  const [paymentMethod, setPaymentMethod] = useState('installments');

  // Start the search while the customer is still here: once the widget
  // holds a complete search (and the date picker is closed), it runs
  // quietly in the background and the results page picks it up when they
  // press Search (src/lib/searchPrefetch.js). While the travelers picker is
  // open it waits; closing it, or closing the date picker, starts the search
  // at once (useSearchPickers). Passengers and cabin are
  // shaped the way the results page reads them back from the URL. Filling
  // it in also re-checks for a newer Defera, which Search would then load
  // (src/lib/searchNavigation.js).
  useEffect(() => {
    checkBuildWhileSearching();
    if (isCalendarOpen || isTravelersOpen) return undefined;
    const params = buildFlightSearchParams({
      origin, destination, from: date?.from, to: date?.to, passengers,
      cabinClass: normalizeCabinClass(cabinClass), canonicalPassengers: true,
    });
    if (!params) return undefined;
    const timer = setTimeout(() => prefetchFlightSearch(params), earlySearchDelay());
    return () => clearTimeout(timer);
  }, [origin, destination, date?.from, date?.to, passengers, cabinClass, isCalendarOpen, isTravelersOpen, earlySearchDelay]);

  /** Visible month in the date-picker (UTC-first-of-month). Drives
   *  the SearchAPI cheapest-per-date fetch below so we only ask for
   *  the window the customer is looking at. Default = today+22 so
   *  disabled early dates don't waste a slot in the 62-day window. */
  const [calendarViewMonth, setCalendarViewMonth] = useState(() => {
    const anchor = new Date();
    anchor.setDate(anchor.getDate() + 22);
    return new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1));
  });

  const {
    prices: calendarPrices,
    lowestPrice: calendarLowestPrice,
    isLoading: calendarPricesLoading,
  } = useFlightCalendarPrices({
    origin,
    destination,
    startDate: calendarViewMonth,
    days: 62,
    cabinClass,
    passengers,
    enabled: isCalendarOpen,
    // Always show round-trip prices on the calendar — matches Google
    // Flights' default UX where the date grid assumes a return trip
    // even before the customer has picked one. Trip length uses the
    // customer's actual range when both dates are picked, otherwise
    // falls back to Google's 7-day default so a blank / half-drawn
    // range still hints at realistic round-trip pricing.
    flightType: 'round_trip',
    tripLengthDays: (date?.from instanceof Date && date?.to instanceof Date)
      ? Math.max(1, Math.round((date.to.getTime() - date.from.getTime()) / 86_400_000))
      : 7,
  });

  /** Sync `calendarViewMonth` to the month the picker will actually
   *  render when it opens. Without this the state persists at its
   *  today+22 default, so re-opening the calendar with a previously-
   *  picked date (e.g. October) fetches prices for the wrong window
   *  and no "$X" hints render on the visible dates. React-day-picker's
   *  `onMonthChange` only fires on user navigation, NOT on the initial
   *  mount / open. This effect fills that gap. */
  useEffect(() => {
    if (!isCalendarOpen) return;
    const anchor = (date?.from instanceof Date && !isNaN(date.from.getTime()))
      ? date.from
      : (() => { const d = new Date(); d.setDate(d.getDate() + 22); return d; })();
    setCalendarViewMonth(
      new Date(Date.UTC(anchor.getFullYear(), anchor.getMonth(), 1))
    );
    // date.from is a local-time Date from react-day-picker, so we read
    // local getFullYear/getMonth (not UTC variants) — otherwise a
    // near-midnight local date can land on a different UTC month and
    // the fetch window would be off by one.
  }, [isCalendarOpen, date?.from]);
  const { balance: milesBalance, isLoading: isMilesLoading } = useMilesBalanceRealtime(user?.id ?? null, { isOwnUser: true });
  const { status: userStatus, isLoading: isStatusLoading } = useUserStatus(user?.id ?? null, { isOwnUser: true });
  // Reserve was discontinued for new users — only existing customers with a wallet
  // balance (or funds allocated to an active Reserve trip) still see Reserve UI.
  // balanceCents feeds the payment picker: without it the picker hides Reserve
  // (it only shows with a balance), so a Reserve customer couldn't choose it here.
  const { isEligible: isReserveEligible, balanceCents: reserveBalanceCents } = useReserveEligibility();

  // Active tier drives the entire miles-card theme. Falls back to 'member'
  // (neutral) so the card looks intentional even for first-time users.
  const activeTierId = userStatus?.active?.tier || 'member';
  const tierTheme = TIER_THEME[activeTierId];
  const tierDef = getTier(activeTierId);

  // Flights + Stays: Defera Zero, Miles, and Reserve are not available — coerce to installments.
  React.useEffect(() => {
    if (tripMode !== 'flight_stay') return;
    if (
      paymentMethod === 'defera_zero' ||
      paymentMethod === 'miles' ||
      paymentMethod === 'miles_installments' ||
      paymentMethod === 'reserve'
    ) {
      setPaymentMethod('installments');
    }
  }, [tripMode, paymentMethod]);

  /** Qualifying spend progress toward next Defera Status tier (aligned with StatusProgressCard). */
  const inProgress = userStatus?.in_progress;
  const nextTierId = inProgress?.next_tier;
  const tierProgressPercent =
    !inProgress
      ? 0
      : !nextTierId
        ? 100
        : Math.min(100, Math.max(0, inProgress.percent_to_next ?? 0));
  const tierProgressCaption = !inProgress
    ? 'Progress to next tier'
    : !nextTierId
      ? 'Highest tier unlocked'
      : 'Progress to next tier';

  // Live search activity animation - uses shared data (200+ names × 26 initials × 250+ destinations = 1.3M+ combos)
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  
  const [searches] = useState(() => {
    const searchList = [];
    for (let i = 0; i < 1200; i++) {
      searchList.push(generateSearchActivity());
    }
    return searchList;
  });
  
  // Sporadic rotation with realistic timing - viral bursts then calm
  useEffect(() => {
    const scheduleNextRotation = () => {
      // Random interval between 1000ms and 8000ms
      // Weighted to create viral moments followed by calm periods
      const rand = Math.random();
      let delay;
      
      if (rand < 0.25) {
        // 25% chance: Viral activity (1000ms - 1500ms) - ~1 per second
        delay = 1000 + Math.random() * 500;
      } else if (rand < 0.5) {
        // 25% chance: Active period (2000ms - 3000ms) - steady pace
        delay = 2000 + Math.random() * 1000;
      } else if (rand < 0.75) {
        // 25% chance: Calm period (4000ms - 6000ms) - slowing down
        delay = 4000 + Math.random() * 2000;
      } else {
        // 25% chance: Very quiet (6000ms - 8000ms) - ~1 per 5-8 seconds
        delay = 6000 + Math.random() * 2000;
      }
      
      const timeout = setTimeout(() => {
        setCurrentSearchIndex((prev) => (prev + 1) % searches.length);
        scheduleNextRotation();
      }, delay);
      
      return timeout;
    };
    
    const timeout = scheduleNextRotation();
    return () => clearTimeout(timeout);
  }, [searches.length]);

  // Safe date formatter with validation
  const formatDate = useCallback((dateValue, formatString) => {
    try {
      if (!dateValue || !(dateValue instanceof Date) || isNaN(dateValue.getTime())) {
        return null;
      }
      return format(dateValue, formatString);
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  }, []);

  // Custom date handler to only allow 2 dates (start and end) - with validation.
  // With both dates picked, a day before the departure moves it and keeps the
  // return; any other day starts a new trip there (src/utils/searchDateRange.js).
  const handleDateSelect = useCallback((selectedDate, tappedDay) => {
    try {
      setDate(nextSearchDateRange(date, selectedDate, tappedDay));
    } catch (error) {
      console.error('Error handling date selection:', error);
      // Keep current state on error
    }
  }, [date]);

  // Check screen size for mobile
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setNumberOfMonths(mobile ? 1 : 2);
      setIsMobile(mobile);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const currentUser = await User.me();
        if (!currentUser || !currentUser.email) {
          // Even if no user, try to get origin from localStorage
          const savedOrigin = localStorage.getItem('lastSearchOrigin');
          if (savedOrigin) {
          }
          setIsLoading(false);
          return;
        }
        
        setUser(currentUser);

        const allBookedFlights = await FlightBooking.filter(
          { created_by: currentUser.email },
          '-created_at'
        );
        
        // Get recent trips (sorted by departure date, limited to 3)
        const trips = [...allBookedFlights]
          .filter(f => f.departure_date)
          .sort((a, b) => new Date(b.departure_date) - new Date(a.departure_date))
          .slice(0, 3);
        setRecentTrips(trips);
        
        // Get most recent origin from recent trips or localStorage
        let mostRecentOrigin = '';
        if (trips.length > 0 && trips[0].departure_city) {
          mostRecentOrigin = trips[0].departure_city;
        } else {
          // Fallback to localStorage
          mostRecentOrigin = localStorage.getItem('lastSearchOrigin') || '';
        }
        
        if (mostRecentOrigin) {
        }

        const [bookedFlights, abandonedTrips] = await Promise.all([
        Promise.resolve(allBookedFlights.slice(0, 5)),
        AbandonedTrip.filter({ user_email: currentUser.email, status: 'in_cart' }, '-created_at', 5)]
        );

        const tripsMap = new Map();
        bookedFlights.forEach((flight) => {
          const key = flight.itinerary_image_url || flight.id;
          if (!tripsMap.has(key)) {
            tripsMap.set(key, []);
          }
          tripsMap.get(key).push(flight);
        });

        const bookedActivity = Array.from(tripsMap.values()).map((tripFlights) => {
          const primaryFlight = tripFlights.find((f) => f.is_primary_flight) || tripFlights[0];
          const actualTripStatus = primaryFlight.trip_status || 'pending';

          return {
            type: actualTripStatus,
            id: primaryFlight.id,
            data: primaryFlight,
            date: primaryFlight.created_date,
            flightOffer: {
              id: primaryFlight.id,
              total_amount: (primaryFlight.total_price || 0).toString(),
              owner: {
                name: primaryFlight.marketing_airline,
                logo_symbol_url: primaryFlight.marketing_airline ? `https://logo.clearbit.com/${primaryFlight.marketing_airline.toLowerCase().replace(/\s+/g, '')}.com` : null
              },
              slices: [{
                segments: [{
                  origin: { iata_code: primaryFlight.departure_city, name: primaryFlight.departure_city },
                  destination: { iata_code: primaryFlight.arrival_city, name: primaryFlight.arrival_city },
                  departing_at: `${primaryFlight.departure_date}T${primaryFlight.departure_time || '12:00'}:00`,
                  arriving_at: `${primaryFlight.arrival_date || primaryFlight.departure_date}T${primaryFlight.arrival_time || '15:00'}:00`,
                  marketing_carrier: { name: primaryFlight.marketing_airline }
                }]
              }]
            }
          };
        });

        const abandonedActivity = abandonedTrips.map((trip) => ({
          type: 'in_cart',
          id: trip.id,
          data: trip,
          date: trip.created_date,
          flightOffer: {
            ...trip.flight_data,
            total_amount: (trip.flight_data?.total_amount || 0).toString()
          }
        }));

        const combinedActivity = [...bookedActivity, ...abandonedActivity].
        sort((a, b) => new Date(b.date) - new Date(a.date)).
        slice(0, 4);

        setRecentActivity(combinedActivity);

        // Filter upcoming flights (departure date in the future), group by trip, then build offers with full date range
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const upcomingFlightsRaw = bookedFlights
          .filter(flight => {
            if (!flight.departure_date) return false;
            const departureDate = new Date(flight.departure_date + 'T00:00:00Z');
            const todayUTC = new Date(today.toISOString().split('T')[0] + 'T00:00:00Z');
            return departureDate >= todayUTC;
          })
          .sort((a, b) => new Date(a.departure_date + 'T00:00:00Z') - new Date(b.departure_date + 'T00:00:00Z'))
          .slice(0, 6); // Get more legs to allow up to 3 trips (round trips = 2 legs each)
        
        // Group by trip (itinerary_image_url or id for one-way)
        const upcomingTripsMap = new Map();
        upcomingFlightsRaw.forEach((flight) => {
          const key = flight.itinerary_image_url || flight.bookingReference || flight.id;
          if (!upcomingTripsMap.has(key)) upcomingTripsMap.set(key, []);
          upcomingTripsMap.get(key).push(flight);
        });
        
        const upcoming = Array.from(upcomingTripsMap.values())
          .sort((a, b) => new Date(a[0].departure_date + 'T00:00:00Z') - new Date(b[0].departure_date + 'T00:00:00Z'))
          .slice(0, 3)
          .map((tripFlights) => {
            const primaryFlight = tripFlights.find((f) => f.is_primary_flight) || tripFlights[0];
            const flightData = primaryFlight.flightData || primaryFlight.flight_data || {};
            const firstSlice = (flightData.slices || [])[0];
            let airlineLogo = null;
            if (firstSlice?.segments?.[0]?.marketing_carrier) {
              const mc = firstSlice.segments[0].marketing_carrier;
              airlineLogo = mc.logo_symbol_url || mc.logo_lockup_url || null;
            }
            
            const slices = tripFlights.map((flight) => {
              const depTime = flight.departure_time?.includes(' ') ? flight.departure_time.split(' ')[1] : (flight.departure_time || '12:00');
              const arrTime = flight.arrival_time?.includes(' ') ? flight.arrival_time.split(' ')[1] : (flight.arrival_time || '15:00');
              const arrDate = (flight.arrival_date || flight.departure_date || '').split(' ')[0] || flight.departure_date;
              return {
                segments: [{
                  origin: { iata_code: flight.departure_city || '', name: flight.departure_city || '' },
                  destination: { iata_code: flight.arrival_city || '', name: flight.arrival_city || '' },
                  departing_at: `${flight.departure_date}T${depTime}:00`,
                  arriving_at: `${arrDate}T${arrTime}:00`,
                  marketing_carrier: {
                    name: flight.marketing_airline || '',
                    logo_symbol_url: airlineLogo,
                    logo_lockup_url: airlineLogo
                  }
                }]
              };
            });
            
            return {
              id: primaryFlight.id,
              bookingReference: primaryFlight.bookingReference || primaryFlight.offerId || '',
              total_amount: (primaryFlight.grand_total_with_hold || primaryFlight.totalPrice || primaryFlight.total_price || 0).toString(),
              owner: { name: primaryFlight.marketing_airline, logo_symbol_url: airlineLogo },
              slices
            };
          });

        setUpcomingFlights(upcoming);

      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);


  // Lazy load videos when in viewport (both mobile and desktop)
  useEffect(() => {
    const desktopVideo = document.querySelector('video[id="hero-video"]');
    const mobileVideo = document.querySelector('video[id="hero-video-mobile"]');
    
    const videos = [desktopVideo, mobileVideo].filter(Boolean);
    if (videos.length === 0) return;

    const observers = videos.map(videoElement => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Load and play video when visible
              videoElement.load();
              videoElement.play().catch(err => {
                console.warn('Video autoplay failed:', err);
              });
              observer.disconnect(); // Stop observing once loaded
            }
          });
        },
        { threshold: 0.25 } // Start loading when 25% visible
      );

      observer.observe(videoElement);

      // Handle time loop at 59 seconds
      const handleTimeUpdate = () => {
        if (videoElement.currentTime >= 59) {
          videoElement.currentTime = 0;
        }
      };
      
      videoElement.addEventListener('timeupdate', handleTimeUpdate);

      return { observer, videoElement, handleTimeUpdate };
    });

    return () => {
      observers.forEach(({ observer, videoElement, handleTimeUpdate }) => {
        observer.disconnect();
        videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      });
    };
  }, []);

  const featuredDestinations = [
  {
    name: "Santorini, Greece",
    image: "https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?q=80&w=2070&auto=format&fit=crop",
    description: "Azure waters meet clifftop luxury"
  },
  {
    name: "Kyoto, Japan",
    image: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?q=80&w=2070&auto=format&fit=crop",
    description: "Ancient temples and modern comfort"
  },
  {
    name: "Swiss Alps",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2070&auto=format&fit=crop",
    description: "Breathtaking alpine experiences"
  },
  {
    name: "Maldives",
    image: "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?q=80&w=2070&auto=format&fit=crop",
    description: "Overwater bungalows in paradise"
  },
  {
    name: "Dubai, UAE",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=2070&auto=format&fit=crop",
    description: "Futuristic luxury in the desert"
  },
  {
    name: "Bali, Indonesia",
    image: "https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?q=80&w=2070&auto=format&fit=crop",
    description: "Tropical temples and rice terraces"
  },
  {
    name: "Paris, France",
    image: "https://images.unsplash.com/photo-1431274172761-fca41d930114?q=80&w=2070&auto=format&fit=crop",
    description: "City of lights and romance"
  },
  {
    name: "Iceland",
    image: "https://images.unsplash.com/photo-1551524164-687a55dd1126?q=80&w=2070&auto=format&fit=crop",
    description: "Nordic glaciers and northern lights"
  }];


  const handleDestinationClick = (destination) => {
    setSelectedDestination(destination);
    setShowDestinationModal(true);
  };

  const closeDestinationModal = () => {
    setShowDestinationModal(false);
    setSelectedDestination(null);
  };

  const handleQuickSearch = (e) => {
    e.preventDefault();
    if (!origin || !destination) return;
    
    // Navigate to search page with query params
    const params = new URLSearchParams();
    params.set('origin', origin);
    params.set('destination', destination);
    if (date.from) {
      const fromDate = formatDate(date.from, 'yyyy-MM-dd');
      if (fromDate) params.set('departureDate', fromDate);
    }
    if (date.to) {
      const toDate = formatDate(date.to, 'yyyy-MM-dd');
      if (toDate) params.set('returnDate', toDate);
    }
    writePassengerParams(params, passengers);
    params.set('cabin', cabinClass);
    params.set('tripMode', tripMode);
    const paymentForUrl =
      tripMode === 'flight_stay' && paymentMethod === 'defera_zero' ? 'installments' : paymentMethod;
    params.set('paymentMethod', paymentForUrl);

    openSearchResults(navigate, `/search?${params.toString()}`);
  };


  // Check if auth_mode=signup query param is present
  const searchParams = new URLSearchParams(location.search);
  const isSignupMode = searchParams.get('auth_mode') === 'signup';

  return (
    <>
      {isSignupMode && <MarketingTags googleAds />}
    <div className="relative">
      {/* Royal blue ambient wash — same treatment as the Rewards page */}
      <RoyalAmbientBackground />
      <div className="mx-auto px-6 md:px-8 max-w-6xl relative z-10">
        {/* Hero Section - Mobile: 500px tall with video scaled to fill, Desktop: 16:9 */}
        <div className="relative w-full mb-8 md:mb-12 h-[500px] md:h-auto">
          <div style={{ paddingTop: '56.25%' }} className="hidden md:block"></div>
          
          {/* Mobile version - Video with lazy loading */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden md:hidden bg-black">
            <video
              id="hero-video-mobile"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              loop
              muted
              playsInline
              preload="none"
              poster="/hero-poster.jpg"
              src="/hero-video.mp4"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none z-10" style={{ backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.3) 60%, transparent 100%)' }} />
            <div className="px-5 py-8 absolute inset-0 flex flex-col justify-end pointer-events-none z-30">
              <div className="px-1 py-1 pointer-events-auto">
                {!isLoading && (
                  <h2 className="text-2xl md:text-4xl lg:text-5xl mb-2 font-light">
                    Welcome, {user?.firstName || 'Traveler'}
                  </h2>
                )}
                {isLoading && (
                  <h2 className="text-2xl md:text-4xl lg:text-5xl mb-2 font-light opacity-0">
                    Welcome
                  </h2>
                )}
                <p className="text-white/90 mb-4 py-1 text-xs">Your journey to extraordinary experiences begins here</p>
                <Link to={createPageUrl('search')}>
                  <Button className="text-white px-4 py-2 text-sm font-medium inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 md:px-6 md:py-3 h-auto md:text-base rounded-full transition-all duration-200 bg-gradient-to-b from-[hsl(228,45%,22%)] to-[hsl(228,67%,12%)] border border-[hsl(228,40%,38%)]/40 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.10),0_8px_24px_-12px_rgba(10,18,51,0.7)] hover:from-[hsl(228,45%,22%)] hover:to-[hsl(228,67%,12%)] hover:border-[hsl(228,35%,58%)]/50">
                    <Plus className="w-4 h-4 mr-2" />
                    Plan New Journey
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          
          {/* Desktop version - Lazy loaded video with poster */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden hidden md:block">
            <video
              id="hero-video"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              loop
              muted
              playsInline
              preload="none"
              poster="/hero-poster.jpg"
              src="/hero-video.mp4"
            />
            <div className="absolute inset-0 bg-gradient-to-t pointer-events-none" style={{ backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.3) 60%, transparent 100%)' }} />
            <div className="px-5 py-8 absolute inset-0 flex flex-col justify-end pointer-events-none">
              <div className="px-1 py-1 pointer-events-auto">
                {!isLoading && (
                  <h2 className="text-2xl md:text-4xl lg:text-5xl mb-2 font-light">
                    Welcome, {user?.firstName || 'Traveler'}
                  </h2>
                )}
                {isLoading && (
                  <h2 className="text-2xl md:text-4xl lg:text-5xl mb-2 font-light opacity-0">
                    Welcome
                  </h2>
                )}
                <p className="text-white/90 mb-4 py-1 text-xs">Your journey to extraordinary experiences begins here</p>
                <Link to={createPageUrl('search')}>
                  <Button className="text-white px-4 py-2 text-sm font-medium inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 md:px-6 md:py-3 h-auto md:text-base rounded-full transition-all duration-200 bg-gradient-to-b from-[hsl(228,45%,22%)] to-[hsl(228,67%,12%)] border border-[hsl(228,40%,38%)]/40 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.10),0_8px_24px_-12px_rgba(10,18,51,0.7)] hover:from-[hsl(228,45%,22%)] hover:to-[hsl(228,67%,12%)] hover:border-[hsl(228,35%,58%)]/50">
                    <Plus className="w-4 h-4 mr-2" />
                    Plan New Journey
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* A trip about to leave or under way, right under the hero, the way
            airline apps open on your next flight (src/utils/tripNow.js). */}
        {user && <TripNowCard className="mb-12" />}

        {/* Upcoming Flights */}
        {upcomingFlights.length > 0 && (
          <div className="mb-8 md:mb-10 -mt-4">
            <h2 className="text-white mb-6 text-2xl font-light">Upcoming</h2>
            <div className="space-y-4">
              {upcomingFlights.map((flight) => (
                <FlightOfferCard
                  key={flight.id}
                  offer={flight}
                  hidePrice={true}
                  status="confirmed"
                  variant="large"
                  gradientBottomColor="#0A1233"
                />
              ))}
            </div>
          </div>
        )}

        {/* Live Search Activity Animation */}
        {/* Not in the iOS app: the names and places are generated, not real searches. */}
        {!isNativeApp && (
        <div className="mb-6 md:mb-8 flex justify-center">
          <div className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-full px-4 py-2 inline-flex items-center gap-2 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
            <AnimatePresence mode="wait">
              <motion.p
                key={currentSearchIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="text-white/70 text-xs md:text-sm font-normal"
              >
                {searches[currentSearchIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>
        )}

        {/* Quick Search Widget - Exposed like search page */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-8 md:mb-10"
        >
          <form
            onSubmit={handleQuickSearch}
            className="relative rounded-3xl border border-white/[0.08] bg-gradient-to-br from-white/[0.05] via-white/[0.02] to-transparent p-4 md:p-6 shadow-[0_0_24px_-8px_rgba(0,0,0,0.3)]"
          >
            {/* Brand radial + top hairline — same glass recipe as the section cards
                so the search widget reads as a designed surface, not loose inputs.
                Decorations are clipped in their own layer (instead of overflow-hidden
                on the form) so the airport autocomplete dropdown can extend past the
                card edge without being cut off. */}
            <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
              <div
                className="absolute inset-0"
                style={{ background: 'radial-gradient(ellipse 70% 120% at 12% 0%, rgba(122,138,191,0.07), transparent 60%)' }}
              />
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/[0.15] to-transparent" />
            </div>
            <div className="relative flex flex-col gap-3">
              {/* Trip Mode Toggle — segmented control instead of three floating pills */}
              <div className="flex md:inline-flex md:self-start w-full md:w-auto gap-1 rounded-full border border-white/10 bg-black/25 p-1 mb-2">
                <button
                  type="button"
                  onClick={() => setTripMode('flight')}
                  className={`flex-1 md:flex-none md:px-5 py-1.5 rounded-full font-normal text-sm transition-all ${
                    tripMode === 'flight' || tripMode === 'flight_stay'
                      ? 'bg-white text-black shadow-sm'
                      : 'text-white/60 hover:bg-white/[0.06] hover:text-white'
                  }`}
                >
                  Flights
                </button>
                <button
                  type="button"
                  onClick={() => setTripMode(tripMode === 'flight_stay' ? 'flight' : 'flight_stay')}
                  className={`flex-1 md:flex-none md:px-5 py-1.5 rounded-full font-normal text-sm transition-all ${
                    tripMode === 'flight_stay'
                      ? 'bg-white text-black shadow-sm'
                      : 'text-white/60 hover:bg-white/[0.06] hover:text-white'
                  }`}
                >
                  Stays
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/explore')}
                  className="flex-1 md:flex-none md:px-5 py-1.5 rounded-full font-normal text-sm transition-all text-white/60 hover:bg-white/[0.06] hover:text-white"
                >
                  Explore
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <AirportInput placeholder="From" value={origin} onChange={setOrigin} className="bg-white/[0.04] text-white border-white/10 rounded-full h-12 hover:bg-white/[0.07] hover:border-white/20 focus-within:bg-white/[0.07] focus-within:border-brand-500/40 transition-colors" />
                <AirportInput placeholder="To" value={destination} onChange={setDestination} className="bg-white/[0.04] text-white border-white/10 rounded-full h-12 hover:bg-white/[0.07] hover:border-white/20 focus-within:bg-white/[0.07] focus-within:border-brand-500/40 transition-colors" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="relative">
                  {isMobile ?
                  <>
                      <Button type="button" variant="outline" onClick={() => setIsCalendarOpen(true)} className="w-full h-12 justify-start text-left font-normal bg-white/[0.04] text-white border-white/10 rounded-full hover:bg-white/[0.07] hover:border-white/20 hover:text-white transition-colors">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date?.from ? date.to ? `${formatDate(date.from, "MMM d") || 'Date'} - ${formatDate(date.to, "MMM d") || 'Date'}` : formatDate(date.from, "MMM d") || 'Date' : <span>Dates</span>}
                      </Button>
                      {isCalendarOpen &&
                    <div className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-brand-950/70 backdrop-blur-sm md:hidden" onClick={() => setIsCalendarOpen(false)}>
                          <div className="rounded-t-2xl sm:rounded-2xl overflow-hidden w-full sm:max-w-sm border border-brand-800/45 bg-brand-950/95 backdrop-blur-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.8)] pb-[env(safe-area-inset-bottom)]" onClick={(e) => e.stopPropagation()}>
                            <CalendarRangeStyles />
                            <CalendarComponent initialFocus mode="range" defaultMonth={date?.from || getMinBookableDate()} selected={date} onSelect={handleDateSelect} onMonthChange={(m) => setCalendarViewMonth(new Date(Date.UTC(m.getFullYear(), m.getMonth(), 1)))} numberOfMonths={1} disabled={[{ before: new Date(new Date().setDate(new Date().getDate() + 22)) }]} dayPrices={calendarPrices} lowestPrice={calendarLowestPrice} isLoading={calendarPricesLoading} />
                            <CalendarDock onDone={() => setIsCalendarOpen(false)} />
                          </div>
                        </div>
                    }
                    </> :

                  <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="w-full h-12 justify-start text-left font-normal bg-white/[0.04] text-white border-white/10 rounded-full hover:bg-white/[0.07] hover:border-white/20 hover:text-white transition-colors">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date?.from ? date.to ? `${formatDate(date.from, "MMM d") || 'Date'} - ${formatDate(date.to, "MMM d, yyyy") || 'Date'}` : formatDate(date.from, "MMM d, yyyy") || 'Date' : <span>Dates</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 rounded-2xl bg-brand-950/95 border-brand-800/45 text-white z-[99] backdrop-blur-xl shadow-2xl" align="start" side="bottom" sideOffset={8}>
                        <CalendarRangeStyles />
                        <CalendarComponent initialFocus mode="range" defaultMonth={date?.from || getMinBookableDate()} selected={date} onSelect={handleDateSelect} onMonthChange={(m) => setCalendarViewMonth(new Date(Date.UTC(m.getFullYear(), m.getMonth(), 1)))} numberOfMonths={numberOfMonths} disabled={[{ before: new Date(new Date().setDate(new Date().getDate() + 22)) }]} dayPrices={calendarPrices} lowestPrice={calendarLowestPrice} isLoading={calendarPricesLoading} />
                        <CalendarDock onDone={() => setIsCalendarOpen(false)} />
                      </PopoverContent>
                    </Popover>
                  }
                </div>
                
                <div className="hidden md:block">
                  <PassengerSelector passengers={passengers} onChange={setPassengers} onOpenChange={onTravelersOpenChange} className="bg-white/[0.04] text-white border-white/10 rounded-full h-12 hover:bg-white/[0.07] hover:border-white/20 transition-colors" />
                </div>
                <div className="hidden md:block">
                  <CabinClassSelector value={cabinClass} onChange={setCabinClass} className="bg-white/[0.04] text-white border-white/10 rounded-full h-12 hover:bg-white/[0.07] hover:border-white/20 transition-colors" />
                </div>
                <div className="hidden md:block">
                  <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} milesBalance={milesBalance} reserveBalanceCents={reserveBalanceCents} hasStay={tripMode === 'flight_stay'} className="bg-white/[0.04] text-white border-white/10 rounded-full h-12 hover:bg-white/[0.07] hover:border-white/20 transition-colors" />
                </div>
              </div>
              
              <div className="flex gap-3 md:hidden">
                <div style={{ width: '15%', minWidth: 0 }}>
                  <PassengerSelector passengers={passengers} onChange={setPassengers} onOpenChange={onTravelersOpenChange} className="bg-white/[0.04] text-white border-white/10 rounded-full h-12 hover:bg-white/[0.07] hover:border-white/20 transition-colors" />
                </div>
                <div style={{ width: '40%', minWidth: 0 }}>
                  <CabinClassSelector value={cabinClass} onChange={setCabinClass} className="bg-white/[0.04] text-white border-white/10 rounded-full h-12 hover:bg-white/[0.07] hover:border-white/20 transition-colors" />
                </div>
                <div style={{ width: '40%', minWidth: 0 }}>
                  <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} milesBalance={milesBalance} reserveBalanceCents={reserveBalanceCents} hasStay={tripMode === 'flight_stay'} className="bg-white/[0.04] text-white border-white/10 rounded-full h-12 hover:bg-white/[0.07] hover:border-white/20 transition-colors" />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={!origin || !destination}
                className="w-full h-12 mt-1 rounded-full font-medium text-white transition-all duration-200 bg-gradient-to-b from-[hsl(228,45%,22%)] to-[hsl(228,67%,12%)] border border-[hsl(228,40%,38%)]/40 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.10),0_8px_24px_-12px_rgba(10,18,51,0.6)] hover:from-[hsl(228,45%,22%)] hover:to-[hsl(228,67%,12%)] hover:border-[hsl(228,35%,58%)]/50 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.14),0_10px_28px_-12px_rgba(10,18,51,0.8)] disabled:opacity-50"
              >
                <SearchIcon className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </form>
        </motion.div>

        {/* Defera Miles + Status — glass shell + SectionBand (Trips modal style). */}
        {user && !isMilesLoading && !isStatusLoading && (milesBalance > 0 || userStatus) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-8"
          >
            <div className="bg-[#161617] rounded-[28px] overflow-hidden">
              <SectionBand
                icon={Sparkles}
                iconColor={tierTheme.accent}
                accentRgb={tierTheme.sectionBandRgb || '148,163,184'}
                title="Miles & Status"
                subtitle={`Earning at ${tierDef.earnRate}`}
              />
              <div className="p-5 sm:p-7 space-y-6">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-white/[0.08] shrink-0">
                      <img
                        src="/defera-logo-web.png"
                        alt="Defera"
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white/45 text-[10px] uppercase tracking-[0.12em] font-normal">
                        Signed in as
                      </p>
                      <h3 className="text-white font-semibold tracking-tight text-lg sm:text-xl truncate">
                        {user.firstName ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}` : user.email?.split('@')[0]}
                      </h3>
                      <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
                        <p className={`text-xs truncate ${tierTheme.text}`}>
                          {tierDef.name}
                        </p>
                        <MyVipBadge />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-row items-center justify-between sm:justify-end gap-4 shrink-0 w-full sm:w-auto">
                    {milesBalance > 0 && (
                      <Button
                        size="sm"
                        onClick={() => navigate('/rewards', { state: { scrollToCalculator: true } })}
                        className="h-9 rounded-full bg-white text-gray-900 hover:bg-white/90 font-medium text-xs px-4"
                      >
                        Redeem
                      </Button>
                    )}
                    <div className="text-right tabular-nums">
                      <div className={`text-3xl sm:text-[34px] font-semibold tracking-tight ${tierTheme.accent}`}>
                        {milesBalance.toLocaleString()}
                      </div>
                      <p className="text-white/40 text-[11px] mt-1">Available miles</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-[11px] text-white/45 mb-2">
                    <span>{tierProgressCaption}</span>
                    <span>{Math.round(tierProgressPercent)}%</span>
                  </div>
                  <div className="relative h-2 bg-white/[0.08] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${tierProgressPercent}%` }}
                      transition={{ duration: 1.5, ease: 'easeOut', delay: 0.35 }}
                      className={`absolute inset-y-0 left-0 rounded-full ${tierTheme.progressFill}`}
                    />
                  </div>
                </div>

                {userStatus && (
                  <>
                    <div className="h-px bg-white/[0.07]" aria-hidden />
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.12em] text-white/38 font-normal mb-3">
                        Status progress
                      </p>
                      <div className="rounded-2xl bg-white/[0.04] p-5 sm:p-6">
                        <StatusProgressCard status={userStatus} variant="embedded" />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* JSX Semi-Private Flights Promotional Image - Mobile Only */}
        <div className="md:hidden mb-8">
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="/jsx-flights-promo.png" 
              alt="Semi-private JSX flights" 
              className="w-full h-auto"
            />
          </div>
        </div>

        {/* Defera Promotional Video - Mobile Only */}
        <div className="md:hidden mb-8">
          <div className="rounded-2xl overflow-hidden bg-black relative" style={{ paddingBottom: '100%' }}>
            <iframe 
              src="https://www.youtube.com/embed/U_y1uuQ2f_s?autoplay=1&mute=1&loop=1&playlist=U_y1uuQ2f_s&controls=0&modestbranding=1&rel=0&iv_load_policy=3"
              className="absolute top-0 left-0 w-full h-full"
              style={{ border: 'none' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Defera Promotional Video"
            />
          </div>
        </div>


        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4 md:gap-5 mb-8 md:mb-10">
          {[
            { icon: Plane, value: recentTrips.length, label: 'Trips Planned', delay: 0 },
            { icon: MapPin, value: 0, label: 'Cities Visited', delay: 0.1 },
          ].map(({ icon: Icon, value, label, delay }) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay }}
              className="rounded-[28px] bg-[#161617] px-6 py-8 text-center"
            >
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.08] mx-auto mb-4">
                <Icon className="w-5 h-5 text-brand-200" strokeWidth={1.8} />
              </span>
              <p className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-1 tabular-nums">{value}</p>
              <p className="text-sm text-white/50">{label}</p>
            </motion.div>
          ))}
        </div>

        {/* Refundable Trip Benefits - Defera Zero, American Airlines & Starbucks */}
        <section className="w-full space-y-6 mb-8 md:mb-10">
          {/* Defera Zero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col lg:flex-row min-h-[380px] rounded-[28px] overflow-hidden bg-[#161617]"
          >
            <div className="lg:w-[45%] relative min-h-[340px] lg:min-h-[380px]">
              <img
                src="/travel1.jpg"
                alt="Travel with Defera Zero"
                className="w-full h-full min-h-[340px] lg:min-h-[380px] object-cover object-center"
              />
            </div>
            <div className="relative flex-1 lg:w-[55%] flex flex-col justify-center items-start p-7 sm:p-10 md:p-14 lg:p-16 overflow-hidden">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-32 -right-32 w-96 h-96"
                style={{ background: 'radial-gradient(circle at center, rgba(61,90,140,0.3), transparent 65%)' }}
              />
              <p className="relative text-sm font-medium text-brand-200 mb-4">Defera Zero</p>
              <h2 className="relative text-[1.75rem] sm:text-[2.125rem] font-semibold text-white mb-3 tracking-tight leading-[1.12]">
                0% down.
                <br />
                Nothing due today.
              </h2>
              <p className="relative text-[15px] md:text-base text-white/80 leading-relaxed mb-7 max-w-xl">
                Book refundable flights with United, American, Southwest, JetBlue, or Virgin Atlantic — pay nothing
                today. Your first payment lands on your first scheduled installment date, and you can cancel up to
                24 hours before departure for 75% back. No credit check, no interest.
              </p>
              <div className="relative flex flex-wrap gap-2.5 mb-8">
                {[
                  { icon: Zap, text: '0% upfront' },
                  { icon: DollarSign, text: 'Equal payments' },
                  { icon: RotateCcw, text: '75% refund if canceled' },
                  { icon: XCircle, text: 'Cancel up to 24h before departure' },
                  { icon: Gift, text: '$10 Starbucks gift card' },
                ].map(({ icon: Icon, text }) => (
                  <span key={text} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.08] text-white/85 text-sm">
                    <Icon className="w-4 h-4 text-brand-200" strokeWidth={1.8} />
                    {text}
                  </span>
                ))}
              </div>
              {tripMode !== 'flight_stay' && (
              <Button
                onClick={() => navigate(createPageUrl('search'), { state: { paymentMethod: 'defera_zero' } })}
                className="relative inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full px-6 py-3 h-auto text-[15px] font-medium text-[#1d1d1f] bg-white hover:bg-white/90 transition-colors"
              >
                Try Defera Zero
                <ArrowRight className="w-4 h-4" />
              </Button>
              )}
            </div>
          </motion.div>

          {/* "Make it refundable" and "Let us treat you": hidden for now,
              see SHOW_REFUNDABLE_PERKS_CARDS. */}
          {SHOW_REFUNDABLE_PERKS_CARDS && (<>
          {/* American Airlines / Main Cabin Extra */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col lg:flex-row min-h-[380px] rounded-[28px] overflow-hidden bg-[#161617]"
          >
              <div className="lg:w-[45%] relative min-h-[340px] lg:min-h-[380px]">
                <img
                  src="/maincabinextra.jpg"
                  alt="Main Cabin Extra seating"
                  className="w-full h-full min-h-[340px] lg:min-h-[380px] object-cover object-[center_70%]"
                />
              </div>
              <div className="flex-1 lg:w-[55%] flex flex-col justify-center items-start p-7 sm:p-10 md:p-14 lg:p-16">
                <img src="/aa3.png" alt="American Airlines" className="h-11 md:h-12 w-auto mb-6 object-contain" />
                <h2 className="text-[1.75rem] sm:text-[2.125rem] font-semibold text-white mb-3 tracking-tight leading-[1.12]">
                  Make it refundable.
                  <br />
                  Get the perks.
                </h2>
                <p className="text-[15px] md:text-base text-white/80 leading-relaxed mb-7 max-w-xl">
                  When you make your American Airlines trip refundable, receive extra legroom seating, priority
                  boarding, complimentary beer, wine &amp; spirits — plus a 75% refund if plans change.
                </p>
                <div className="flex flex-wrap gap-2.5 mb-8">
                  {[
                    { icon: Armchair, text: 'Extra legroom' },
                    { icon: Plane, text: 'Priority boarding' },
                    { icon: Wine, text: 'Complimentary drinks' },
                    { icon: Gift, text: '$10 Starbucks Gift Card' },
                  ].map(({ icon: Icon, text }) => (
                    <span key={text} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.08] text-white/85 text-sm">
                      <Icon className="w-4 h-4 text-brand-200" strokeWidth={1.8} />
                      {text}
                    </span>
                  ))}
                </div>
                <Link to={createPageUrl('search')} className="w-full sm:w-auto">
                  <Button className="inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full px-6 py-3 h-auto text-[15px] font-medium text-[#1d1d1f] bg-white hover:bg-white/90 transition-colors">
                    Search Flights
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
          </motion.div>

          {/* All Other Flights / Starbucks */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col lg:flex-row min-h-[380px] rounded-[28px] overflow-hidden bg-[#101b14]"
          >
              <div className="lg:w-[45%] relative min-h-[340px] lg:min-h-[380px]">
                <img
                  src="/starbucks.jpg"
                  alt="Starbucks"
                  className="w-full h-full min-h-[340px] lg:min-h-[380px] object-cover object-center"
                />
              </div>
              <div className="relative flex-1 lg:w-[55%] flex flex-col justify-center items-start p-7 sm:p-10 md:p-14 lg:p-16 overflow-hidden">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-32 -right-32 w-96 h-96"
                  style={{ background: 'radial-gradient(circle at center, rgba(0,112,74,0.35), transparent 65%)' }}
                />
                <img src="/starbucks2.png" alt="Starbucks" className="relative h-11 md:h-12 w-auto mb-6 object-contain" />
                <h2 className="relative text-[1.75rem] sm:text-[2.125rem] font-semibold text-white mb-3 tracking-tight leading-[1.12]">
                  Let us treat you.
                </h2>
                <p className="relative text-[15px] md:text-base text-white/80 leading-relaxed mb-7 max-w-xl">
                  Book refundable on any U.S. based airline and receive a $10 Starbucks Gift Card with every booking.
                </p>
                <span className="relative inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/[0.08] mb-8">
                  <img src="/stars.png" alt="" className="w-8 h-8 object-contain flex-shrink-0" />
                  <span className="font-medium text-white text-sm whitespace-nowrap">$10 Starbucks Gift Card</span>
                </span>
                <Link to={createPageUrl('search')} className="relative w-full sm:w-auto">
                  <Button className="inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full px-6 py-3 h-auto text-[15px] font-medium text-white bg-[#00704a] hover:bg-[#005a38] transition-colors">
                    Search Flights
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <p className="relative text-[9px] text-white/25 leading-tight mt-6 max-w-xl">
                  Dispersed upon completion of the installment plan. Restrictions apply. Offer subject to availability and may be modified or discontinued at any time without notice. Terms and conditions apply.
                </p>
              </div>
          </motion.div>
          </>)}
        </section>

        {/* Featured Destinations - No Boxes */}
        <div className="mb-8 md:mb-10">
          <h2 className="text-white text-2xl md:text-3xl font-semibold tracking-tight mb-6 md:mb-8">
            Curated destinations.
          </h2>

          <div className="overflow-x-auto pb-4 -mx-4 px-4">
            <div className="flex gap-4 md:gap-5 w-max">
              {featuredDestinations.map((destination) =>
              <div
                key={destination.name}
                className="flex-shrink-0 w-80 cursor-pointer group"
                onClick={() => handleDestinationClick(destination)}>

                  <div className="relative h-96 rounded-[28px] overflow-hidden">
                    <img
                    src={destination.image}
                    alt={destination.name}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" />

                    {/* Subtle base gradient, deepens on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10 transition-opacity duration-500" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-500" />

                    {/* Top-left location pill */}
                    <div className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full bg-black/35 backdrop-blur-md border border-white/10 px-3 py-1.5">
                      <MapPin className="w-3 h-3 text-white/80" />
                      <span className="text-white/90 text-[11px] font-medium tracking-wide">{destination.name.split(',')[0]}</span>
                    </div>

                    {/* Bottom glass panel */}
                    <div className="absolute inset-x-3 bottom-3">
                      <div className="rounded-[20px] bg-white/10 backdrop-blur-xl border border-white/10 p-4 transition-colors duration-500 group-hover:bg-white/[0.14]">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="text-white font-semibold text-base tracking-tight truncate">{destination.name}</h3>
                            <p className="text-white/70 text-xs leading-relaxed mt-0.5">{destination.description}</p>
                          </div>
                          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/15 border border-white/15 flex items-center justify-center transition-all duration-500 group-hover:bg-white group-hover:border-white">
                            <ArrowRight className="w-4 h-4 text-white transition-colors duration-500 group-hover:text-black" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* World's-first banners */}
        <div className="mb-8 md:mb-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              icon: Award,
              title: 'Exclusive rewards.',
              desc: 'The world\u2019s first travel layaway with a reward system — triple dip with credit card points, frequent flyer miles, and Defera Miles.',
            },
            {
              icon: PlaneTakeoff,
              title: 'Semi-private flights.',
              desc: 'The world\u2019s first travel layaway offering JSX semi-private flights with flexible payment plans.',
            },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="relative rounded-[28px] bg-[#161617] p-7 sm:p-8 overflow-hidden">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-24 -right-24 w-72 h-72"
                style={{ background: 'radial-gradient(circle at center, rgba(61,90,140,0.22), transparent 65%)' }}
              />
              <span className="relative flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.08] mb-5">
                <Icon className="w-5 h-5 text-brand-200" strokeWidth={1.8} />
              </span>
              <p className="relative text-[11px] uppercase tracking-[0.14em] text-white/40 font-normal mb-2">World's first</p>
              <h3 className="relative text-xl md:text-2xl font-semibold tracking-tight text-white leading-tight mb-2">{title}</h3>
              <p className="relative text-[15px] text-white/80 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        {/* Complimentary Upgrades Section */}
        <motion.div
          className="mb-8 md:mb-10 relative overflow-hidden bg-[#161617] rounded-[28px]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-40 -right-40 w-[30rem] h-[30rem]"
            style={{ background: 'radial-gradient(circle at center, rgba(61,90,140,0.3), transparent 65%)' }}
          />

          <div className="relative flex flex-col md:flex-row">
            {/* Left — text content */}
            <div className="flex-1 p-7 sm:p-10 md:p-12 flex flex-col justify-center">
              <p className="text-sm font-medium text-brand-200 mb-4">World's first in layaway</p>

              <h2 className="text-[2rem] sm:text-[2.5rem] md:text-5xl font-semibold tracking-tight leading-[1.1] mb-4">
                <span
                  style={{
                    background: 'linear-gradient(110deg, #EEF1F8 0%, #D5DCEF 35%, #A8B5DB 65%, #6B8BC4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Book your trip.
                  <br />
                  Get upgraded.
                </span>
              </h2>

              <p className="text-[15px] md:text-base text-white/80 leading-relaxed mb-7 max-w-md">
                When you book with Defera, you're not just securing a seat — you're opening the door to
                complimentary cabin upgrades, arranged by our team as a thank-you for being part of the Defera
                community.
              </p>

              <Link
                to="/search"
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-medium text-[#1d1d1f] bg-white hover:bg-white/90 transition-colors w-full sm:w-fit"
              >
                <span>Start booking</span>
                <ArrowRight className="w-4 h-4" />
              </Link>

              <p className="mt-7 text-[10px] text-white/25 leading-snug max-w-xs">
                * Complimentary upgrades are not guaranteed and are subject to availability. Eligibility is determined by a range of internal factors and is assessed on a per-booking basis.
              </p>
            </div>

            {/* Right — image column */}
            <div className="relative w-full md:w-[40%] flex-shrink-0 flex items-center justify-center px-6 pb-12 md:px-0 md:pb-0 min-h-[260px] md:min-h-0">
              <img
                src="/deferaupgrades.jpg"
                alt="Complimentary upgrade"
                className="relative w-full max-w-sm md:max-w-none md:w-[78%] object-contain"
                style={{
                  WebkitMaskImage:
                    'radial-gradient(ellipse 70% 75% at 50% 50%, #000 40%, rgba(0,0,0,0.6) 70%, transparent 100%)',
                  maskImage:
                    'radial-gradient(ellipse 70% 75% at 50% 50%, #000 40%, rgba(0,0,0,0.6) 70%, transparent 100%)',
                }}
              />
            </div>
          </div>
        </motion.div>

        {/* Three Ways to Book */}
        <motion.div
          className="mb-8 md:mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
        >
          <div className="text-center mb-8 md:mb-10">
            <p className="text-sm md:text-base font-medium text-white mb-3">Payment plans</p>
            <h2 className="text-[2rem] sm:text-[2.5rem] md:text-5xl font-semibold tracking-tight leading-[1.08] mb-3">
              <span
                style={{
                  background: 'linear-gradient(110deg, #EEF1F8 0%, #D5DCEF 35%, #A8B5DB 65%, #6B8BC4 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {isReserveEligible ? 'Three ways to book.' : 'Two ways to book.'}
              </span>
            </h2>
            <p className="text-white/80 text-[15px] md:text-base max-w-xl mx-auto leading-relaxed">
              Every traveler is different. Pick the plan that fits your trip, your budget, and your style.
            </p>
          </div>

          <div className={`grid grid-cols-1 ${isReserveEligible ? 'md:grid-cols-3' : 'md:grid-cols-2 max-w-3xl mx-auto'} gap-4 md:gap-5`}>

              {/* Defera Installments */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.55, delay: 0.05, ease: [0.22, 0.61, 0.36, 1] }}
                className="relative rounded-[28px] overflow-hidden bg-[#161617] flex flex-col"
              >
                <div className="p-7 sm:p-8 relative flex flex-col flex-1">
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.08] mb-5">
                    <CreditCard className="w-5 h-5 text-brand-200" strokeWidth={1.8} />
                  </span>
                  <h3 className="text-xl font-semibold text-white mb-2 tracking-tight">Defera Installments</h3>
                  <p className="text-white/80 text-[13px] leading-relaxed mb-5 flex-1">
                    Pay an initial deposit to secure your agreed-upon trip price, then spread the remaining balance over weekly or bi-weekly installments. Your price is protected from the moment your booking is approved. E-ticket released when paid in full.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {['Trip price secured at booking', 'Small initial deposit required', 'Weekly or bi-weekly installments', 'No credit check, no interest', 'E-ticket released when fully paid'].map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-[12px] text-white/80">
                        <CheckCircle className="w-3.5 h-3.5 text-brand-200/60 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <button type="button" onClick={() => navigate('/search')} className="w-full bg-white/[0.08] hover:bg-white/[0.12] text-white rounded-full py-2.5 text-sm font-medium transition-colors duration-200">
                    Start Booking
                  </button>
                </div>
              </motion.div>

              {/* Defera Zero */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.55, delay: 0.12, ease: [0.22, 0.61, 0.36, 1] }}
                className="relative rounded-[28px] overflow-hidden bg-[#161617] flex flex-col"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-24 -right-24 w-72 h-72"
                  style={{ background: 'radial-gradient(circle at center, rgba(61,90,140,0.3), transparent 65%)' }}
                />
                <div className="p-7 sm:p-8 relative flex flex-col flex-1">
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.08] mb-5">
                    <Zap className="w-5 h-5 text-brand-200" strokeWidth={1.8} />
                  </span>
                  <h3 className="text-xl font-semibold text-white mb-2 tracking-tight">Defera Zero</h3>
                  <p className="text-white/80 text-[13px] leading-relaxed mb-4">
                    Like Installments, but with nothing due at checkout. Refundable fares only — your trip price is secured once approved, first payment not due until your first installment date. Cancel up to 24 hrs before departure for 75% back.
                  </p>
                  <div className="mb-4">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-white/35 font-normal mb-2">Available on</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      {[
                        { name: 'United', logo: 'https://www.united.com/favicon.ico' },
                        { name: 'American', logo: 'https://www.aa.com/favicon.ico' },
                        { name: 'Southwest', logo: 'https://www.southwest.com/favicon.ico' },
                        { name: 'JetBlue', logo: 'https://www.jetblue.com/favicon.ico' },
                        { name: 'Virgin Atlantic', logo: 'https://www.virginatlantic.com/favicon.ico' },
                      ].map(({ name, logo }) => (
                        <div key={name} className="flex items-center gap-1.5">
                          <img src={logo} alt={name} className="w-3.5 h-3.5 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                          <span className="text-[11px] text-white/50 font-normal">{name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <ul className="space-y-2 mb-6 flex-1">
                    {['Trip price secured at booking', 'Zero down — nothing due at checkout', 'Refundable fares only', 'No credit check, no interest', 'E-ticket released when fully paid'].map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-[12px] text-white/80">
                        <CheckCircle className="w-3.5 h-3.5 text-brand-200/60 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <button type="button" onClick={() => navigate('/search')} className="w-full mt-auto bg-white hover:bg-white/90 text-[#1d1d1f] font-medium rounded-full py-2.5 text-sm transition-colors duration-200">
                    Search Defera Zero Flights
                  </button>
                </div>
              </motion.div>

              {/* Defera Reserve — Discontinued for new users; rendered only for
                  existing customers with a wallet balance or active Reserve trip. */}
              {isReserveEligible && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.55, delay: 0.19, ease: [0.22, 0.61, 0.36, 1] }}
                className="relative rounded-[28px] overflow-hidden bg-[#161617] flex flex-col"
              >
                <div className="p-7 sm:p-8 relative flex flex-col flex-1">
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.08] mb-5">
                    <RefreshCw className="w-5 h-5 text-brand-200" strokeWidth={1.8} />
                  </span>
                  <h3 className="text-xl font-semibold text-white mb-2 tracking-tight">Defera Reserve</h3>
                  <p className="text-white/80 text-[13px] leading-relaxed mb-5 flex-1">
                    Pay toward a trip before you ticket it. Fare stays live until you choose to ticket. Price drops? You keep the savings. Price goes up? Switch flights, no penalty. Ticket when your plan is fully paid or top up at any time.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {['Fare stays live — not locked until ticketed', 'Price drops? You keep the savings', 'Switch flights anytime before ticketing', 'No credit check, no interest', 'E-ticket released when fully paid or topped up'].map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-[12px] text-white/80">
                        <CheckCircle className="w-3.5 h-3.5 text-brand-200/60 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <button type="button" onClick={() => navigate('/app/reserve')} className="w-full bg-white/[0.08] hover:bg-white/[0.12] text-white rounded-full py-2.5 text-sm font-medium transition-colors duration-200">
                    Go to Reserve
                  </button>
                </div>
              </motion.div>
              )}

          </div>
        </motion.div>

        {/* The Beauty of Freedom Section */}
        <motion.div 
          className="mb-8 md:mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
        >
          <div className="text-center mb-8 md:mb-10 px-4">
            <h2 className="text-white text-2xl md:text-3xl font-semibold tracking-tight mb-3">
              The beauty of freedom.
            </h2>
            <p className="text-white/80 text-[15px] md:text-base max-w-2xl mx-auto leading-relaxed">
              Travel is more than movement — it's the art of expansion. Each journey unlocks new perspectives
              and reveals the infinite possibilities beyond familiar horizons.
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto rounded-[28px] overflow-hidden bg-[#161617]">
            <BeautyOfFreedomVideo />
          </div>
        </motion.div>

        {/* Empty State for New Users */}
        {recentActivity.length === 0 && !isLoading &&
        <motion.div 
          className="mb-8 md:mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="rounded-[28px] bg-[#161617] p-12 md:p-16 text-center">
            <span className="flex items-center justify-center w-14 h-14 rounded-full bg-white/[0.08] mx-auto mb-6">
              <Calendar className="w-6 h-6 text-brand-200" strokeWidth={1.8} />
            </span>
            <h3 className="text-2xl font-semibold tracking-tight text-white mb-2">Begin your journey.</h3>
            <p className="text-[15px] mb-8 max-w-md mx-auto text-white/60 leading-relaxed">
              Start your journey by searching for flights.
            </p>
            <Link to={createPageUrl('search')}>
              <Button className="bg-white hover:bg-white/90 text-[#1d1d1f] font-medium px-6 py-3 h-auto rounded-full">
                <Plane className="w-4 h-4 mr-2" />
                Search Flights
              </Button>
            </Link>
          </div>
        </motion.div>
        }
      </div>

      {/* Destination Modal */}
      <DestinationModal
        destination={selectedDestination}
        isOpen={showDestinationModal}
        onClose={closeDestinationModal} />

      {/* Footer - Apple Dark Gray Style */}
      <footer className="relative z-10 w-full bg-black py-28 md:py-32 px-6 text-xs text-zinc-600">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-8 mb-16">
            <div className="col-span-2">
              <img
                src="/defera-logo-web.png"
                alt="Defera"
                className="h-8 md:h-10 opacity-80 mb-6"
              />
              <p className="text-zinc-600 leading-relaxed max-w-sm">
                Defera is the leading Buy Now, Pay Later platform for travel. We empower travelers to explore the world with flexible payment plans, no hidden fees, and no credit checks.
              </p>
            </div>

            <div>
              <h4 className="text-zinc-600 font-medium mb-4">Company</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/about')} className="text-zinc-600 hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => navigate('/careers')} className="text-zinc-600 hover:text-white transition-colors">Careers</button></li>
                <li><button onClick={() => navigate('/press')} className="text-zinc-600 hover:text-white transition-colors">Press</button></li>
                <li><button onClick={() => navigate('/contact')} className="text-zinc-600 hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-zinc-600 font-medium mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/how-it-works')} className="text-zinc-600 hover:text-white transition-colors">How it Works</button></li>
                <li><button onClick={() => navigate('/rewards')} className="text-zinc-600 hover:text-white transition-colors">Rewards</button></li>
                <li><button onClick={() => navigate('/help')} className="text-zinc-600 hover:text-white transition-colors">Help Center</button></li>
                <li><button onClick={() => navigate('/security')} className="text-zinc-600 hover:text-white transition-colors">Security</button></li>
                <li><button onClick={() => navigate('/guides')} className="text-zinc-600 hover:text-white transition-colors">Guides</button></li>
                <li><button onClick={() => navigate('/terms')} className="text-zinc-600 hover:text-white transition-colors">Terms of Service</button></li>
                <li><button onClick={() => navigate('/privacy')} className="text-zinc-600 hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/privacy-choices')} className="text-zinc-600 hover:text-white transition-colors">Do Not Sell or Share My Personal Information</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 space-y-6">
            <div className="space-y-2.5">
              <p className="font-normal text-zinc-600">
                Copyright © {new Date().getFullYear()} Defera LLC. All Rights Reserved
              </p>
              <p className="text-[10px] font-normal leading-tight text-zinc-600 max-w-4xl">
                Defera LLC is partnered with a fully accredited organization in multiple countries that maintains direct and indirect relationships with various airline carriers and hotel chains. Some carriers and hotels may not be available for booking due to the absence of an established partnership. All cancellation and change policies are governed by the respective airline and not by Defera LLC. Defera LLC is a financial technology company providing payment processing and installment services. Defera LLC is not a bank, credit union, or other depository institution. All financial services are provided by our partner financial institutions. Terms and conditions apply to all services. Please review our Terms of Service and Privacy Policy for complete information regarding our services, data practices, and user obligations.
              </p>
            </div>
            
            <div className="flex items-center gap-6 pt-2">
              <button onClick={() => navigate('/home?auth_mode=login')} className="text-zinc-600 hover:text-white transition-colors">
                Sign In
              </button>
              <span className="text-zinc-600">|</span>
              <button onClick={() => navigate('/home?auth_mode=signup')} className="text-zinc-600 hover:text-white transition-colors">
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );

}
